import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, Loader2, CheckCircle2, XCircle, AlertCircle, FileText,
  Mic, Zap, Filter, RotateCcw,
} from 'lucide-react';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  cdr_ingested:          { label: 'Call ingested',       icon: FileText,    color: 'text-sky-300',    bg: 'bg-sky-500/10',    border: 'border-sky-500/30' },
  cdr_skipped:           { label: 'CDR skipped',         icon: AlertCircle, color: 'text-white/40',   bg: 'bg-white/5',       border: 'border-white/10' },
  transcript_fetched:    { label: 'Transcript fetched',  icon: CheckCircle2, color: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  transcript_failed:     { label: 'Transcript failed',   icon: XCircle,     color: 'text-rose-300',   bg: 'bg-rose-500/10',   border: 'border-rose-500/30' },
  transcript_unavailable:{ label: 'No transcript (3x)',  icon: XCircle,     color: 'text-amber-300',  bg: 'bg-amber-500/10',  border: 'border-amber-500/30' },
  recording_captured:    { label: 'Recording captured',  icon: Mic,         color: 'text-indigo-300', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30' },
  graded:                { label: 'Graded',              icon: Zap,         color: 'text-violet-300', bg: 'bg-violet-500/10', border: 'border-violet-500/30' },
  grading_failed:        { label: 'Grading failed',      icon: XCircle,     color: 'text-rose-300',   bg: 'bg-rose-500/10',   border: 'border-rose-500/30' },
};

const FILTER_OPTIONS = [
  { key: '',                                  label: 'All events' },
  { key: 'cdr_ingested',                      label: 'Ingested' },
  { key: 'transcript_fetched,recording_captured', label: 'Transcript + Recording' },
  { key: 'transcript_failed,transcript_unavailable,grading_failed', label: 'Failures only' },
  { key: 'graded',                            label: 'Graded' },
];

const SummaryCard = ({ label, count, accent }) => (
  <div className={`rounded-xl border ${accent} px-4 py-3 bg-white/5`}>
    <p className="text-xs uppercase tracking-wider text-white/40 font-semibold">{label}</p>
    <p className="text-2xl font-bold text-white mt-1">{count}</p>
  </div>
);

const SyncLogRow = ({ ev, onCallClick, onRetry, retryingId }) => {
  const meta = KIND_META[ev.kind] || { label: ev.kind, icon: AlertCircle, color: 'text-white/40', bg: 'bg-white/5', border: 'border-white/10' };
  const Icon = meta.icon;
  const when = ev.created_at ? new Date(ev.created_at).toLocaleString() : '—';
  const isFailure = ['transcript_failed', 'transcript_unavailable', 'grading_failed'].includes(ev.kind);
  const busy = retryingId === ev.call_id;
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`sync-log-row-${ev.kind}`}>
      <td className="px-3 py-2.5 whitespace-nowrap text-xs text-white/40 font-mono">{when}</td>
      <td className="px-3 py-2.5">
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.color} border ${meta.border}`}>
          <Icon className="w-3 h-3" />
          {meta.label}
        </span>
      </td>
      <td className="px-3 py-2.5">
        {ev.call_id ? (
          <button
            onClick={() => onCallClick(ev.call_id)}
            className="text-teal-300 hover:text-teal-200 text-xs font-mono underline-offset-2 hover:underline truncate max-w-[260px] block"
            title={ev.call_id}
          >
            {ev.call_id.length > 28 ? ev.call_id.slice(0, 28) + '…' : ev.call_id}
          </button>
        ) : <span className="text-white/30 text-xs">—</span>}
      </td>
      <td className="px-3 py-2.5 text-xs text-white/70 truncate max-w-[200px]">
        {ev.agent_name || <span className="text-white/30">—</span>}
        {ev.extension && <span className="text-white/40 ml-1">ext {ev.extension}</span>}
      </td>
      <td className="px-3 py-2.5 text-xs text-white/60 max-w-[420px]">{ev.message}</td>
      <td className="px-3 py-2.5 text-xs whitespace-nowrap">
        {ev.transcript_chars != null && ev.transcript_chars > 0 && (
          <span className="text-emerald-300/80">{ev.transcript_chars.toLocaleString()} chars</span>
        )}
        {ev.has_recording_url === true && (
          <span className="text-indigo-300/80 ml-2">🎙 rec</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs text-rose-300/80 max-w-[260px] truncate" title={ev.error || ''}>
        {ev.error || ''}
      </td>
      <td className="px-3 py-2.5 text-right">
        {ev.call_id && isFailure && (
          <button
            onClick={() => onRetry(ev.call_id)}
            disabled={busy}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-500/30 disabled:opacity-50"
            data-testid={`sync-log-retry-call-${ev.call_id}`}
            title="Reset this call and re-run transcript fetch (+ Whisper fallback) and grading"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
            Retry
          </button>
        )}
      </td>
    </tr>
  );
};

const AdminSyncLogPage = () => {
  const { domainId } = useParams();
  const navigate = useNavigate();
  // Auth is handled globally by axios.defaults.headers — see AuthContext.
  // Do NOT read localStorage here; the JWT is in-memory + httpOnly cookie.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter ? `&kinds=${encodeURIComponent(filter)}` : '';
      const r = await axios.get(`${API}/admin/domains/${domainId}/sync-log?limit=300${qs}`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load sync log');
    } finally {
      setLoading(false);
    }
  }, [domainId, filter]);

  useEffect(() => { load(); }, [load]);

  const [retryingNoTx, setRetryingNoTx] = useState(false);

  const handleRetryStalled = async () => {
    if (!window.confirm("Retry every stalled call in this domain?\n\nThis resets calls that were marked 'no_transcript' or stuck after 3+ failed grading attempts. They'll be re-queued through transcription + grading from scratch.")) return;
    setRetrying(true);
    try {
      const r = await axios.post(`${API}/admin/domains/${domainId}/retry-stalled`, {});
      toast.success(r.data.message);
      setTimeout(load, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  const handleRetryNoTranscript = async () => {
    if (!window.confirm(
      "Retry every 'no_transcript' call in this domain?\n\n"
      + "For each stuck call I'll ask Yabbit for a fresh recording URL, "
      + "run Whisper on the audio, and re-queue the transcript into grading. "
      + "Calls tried in the last 72h are skipped so this is safe to spam.\n\n"
      + "Watch the log below for per-call outcomes.",
    )) return;
    setRetryingNoTx(true);
    try {
      const r = await axios.post(`${API}/admin/domains/${domainId}/retry-no-transcript`, {});
      toast.success(r.data.message || `Queued ${r.data.queued} calls`);
      // Log entries stream in over the next few seconds — refresh once and
      // the summary event will show up automatically.
      setTimeout(load, 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingNoTx(false);
    }
  };

  const handleRetryCall = async (callId) => {
    setRetryingId(callId);
    try {
      const r = await axios.post(`${API}/admin/calls/${callId}/retry-transcript`, {});
      toast.success(r.data.message || `Re-queued ${callId}`);
      setTimeout(load, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Single retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  const summary = useMemo(() => data?.summary_24h?.by_kind || {}, [data]);

  return (
    <AdminLayout>
      <div className="p-6 space-y-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/admin/domains/${domainId}`)}
            className="text-white/60 hover:text-white p-1.5 -ml-1.5 rounded-lg hover:bg-white/5"
            data-testid="sync-log-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-white">Sync Log</h1>
          <span className="text-white/40 text-sm">Pipeline audit trail · last 24h summary + 300 most recent events</span>
          <button
            onClick={handleRetryNoTranscript}
            disabled={retryingNoTx || loading}
            className="ml-auto inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-teal-500/15 hover:bg-teal-500/25 text-teal-200 border border-teal-500/30 disabled:opacity-50"
            data-testid="sync-log-retry-no-transcript-btn"
            title="Ask Yabbit for fresh recording URLs on every no_transcript call, then re-run Whisper. 72h back-off prevents duplicate attempts."
          >
            {retryingNoTx ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
            Retry no-transcript
          </button>
          <button
            onClick={handleRetryStalled}
            disabled={retrying || loading}
            className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-500/30 disabled:opacity-50"
            data-testid="sync-log-retry-stalled-btn"
            title="Reset every 'no_transcript' or repeatedly-failed call and re-queue it through the pipeline"
          >
            {retrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
            Retry Stalled
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white border border-white/10"
            data-testid="sync-log-refresh-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="sync-log-summary">
          <SummaryCard label="Ingested"          count={summary.cdr_ingested || 0}         accent="border-sky-500/30" />
          <SummaryCard label="Transcripts ✓"     count={summary.transcript_fetched || 0}  accent="border-emerald-500/30" />
          <SummaryCard label="Transcripts ✗"     count={(summary.transcript_failed || 0) + (summary.transcript_unavailable || 0)} accent="border-rose-500/30" />
          <SummaryCard label="Recordings"        count={summary.recording_captured || 0}  accent="border-indigo-500/30" />
          <SummaryCard label="Graded"            count={summary.graded || 0}              accent="border-violet-500/30" />
          <SummaryCard label="Grading errors"    count={summary.grading_failed || 0}      accent="border-rose-500/30" />
        </div>

        <div className="flex items-center gap-2 flex-wrap" data-testid="sync-log-filters">
          <Filter className="w-4 h-4 text-white/40" />
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.key || 'all'}
              onClick={() => setFilter(opt.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filter === opt.key
                  ? 'bg-teal-500/20 text-teal-200 border-teal-500/40'
                  : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
              }`}
              data-testid={`sync-log-filter-${opt.key || 'all'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/[0.03] text-left">
                <tr className="border-b border-white/10">
                  <th className="px-3 py-2.5 text-xs font-semibold text-white/40 uppercase tracking-wider">When</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Event</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Call ID</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Agent</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Message</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Payload</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-white/40 uppercase tracking-wider">Error</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-white/40 uppercase tracking-wider text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-white/40">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && data?.events?.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-white/40" data-testid="sync-log-empty">
                    No events match the current filter. Run a sync to populate the log.
                  </td></tr>
                )}
                {!loading && data?.events?.map((ev, idx) => (
                  <SyncLogRow
                    key={`${ev.created_at}-${idx}`}
                    ev={ev}
                    onCallClick={(cid) => navigate(`/calls/${cid}`)}
                    onRetry={handleRetryCall}
                    retryingId={retryingId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSyncLogPage;
